import { createSelector } from 'reselect';
import { RootState } from 'src/store';
import {
    buildSizeChartFromProduct,
    buildSizeChartFromLinkedAttributes,
    resolveBodyWidthMm,
    resolveBodyLengthMm,
    resolveLinkedBodyWidthMm,
    resolveLinkedBodyLengthMm,
} from 'src/pages/editor/components/decorationSpecs/productPropertySizeChart';
import { getProductDetails } from './getSurfaceProperties';

const getDecorationSpecsState = (state: RootState) => state.pages.editor.decorationSpecs;

export const getProductModel = createSelector([getDecorationSpecsState], (state) => state.productModel);

// TODO(linked-product-fallback): remove `getLinkedSizeAttributes` and both fallback branches
// below alongside the rest of this fallback — see `linkedMerchandisedProductClient.ts`'s module
// doc.
/** Width/Length resolved via the linked-merchandised-product fallback (see
 * `productPropertySizeChart.ts`'s module doc) — only ever populated when the CIM's own product
 * definition doesn't declare Width/Length itself. */
const getLinkedSizeAttributes = createSelector([getDecorationSpecsState], (state) => state.linkedSizeAttributes);

export const getSizeChart = createSelector(
    [getProductModel, getProductDetails, getLinkedSizeAttributes],
    (productModel, surfaceDetails, linkedSizeAttributes) => {
        // Only derive size chart if a product is actually selected/added into the editor (isReady true)
        if (!surfaceDetails?.isReady) {
            return null;
        }
        const selections = surfaceDetails.merchantVariables ?? {};
        const ownSizeChart = buildSizeChartFromProduct(productModel, selections);
        // Fall back to the linked merchandised product's Width/Length when the CIM's own
        // definition has neither — a single-size row, not a full table (see
        // `buildSizeChartFromLinkedAttributes`'s own doc comment for why, including why it's
        // guarded against the *current* Size here rather than trusting the saga's own timing).
        return ownSizeChart ?? buildSizeChartFromLinkedAttributes(linkedSizeAttributes, selections.Size ?? '');
    },
);

export const getBodyMeasurements = createSelector(
    [getProductModel, getProductDetails, getLinkedSizeAttributes],
    (productModel, surfaceDetails, linkedSizeAttributes) => {
        if (!surfaceDetails?.isReady) {
            return {
                bodyWidthMm: undefined,
                bodyLengthMm: undefined,
            };
        }
        const selections = surfaceDetails.merchantVariables ?? {};
        const size = selections.Size ?? '';
        return {
            bodyWidthMm:
                resolveBodyWidthMm(productModel, selections, size) ??
                resolveLinkedBodyWidthMm(linkedSizeAttributes, size),
            bodyLengthMm:
                resolveBodyLengthMm(productModel, selections, size) ??
                resolveLinkedBodyLengthMm(linkedSizeAttributes, size),
        };
    },
);

const getDecorationSpecs = createSelector(
    [getDecorationSpecsState, getSizeChart, getBodyMeasurements],
    (decorationSpecsState, sizeChart, bodyMeasurements) => ({
        ...decorationSpecsState,
        sizeChart,
        bodyWidthMm: bodyMeasurements.bodyWidthMm,
        bodyLengthMm: bodyMeasurements.bodyLengthMm,
    }),
);

export default getDecorationSpecs;
